import { FilterNames, getFiltersData } from 'components/common/GlobalFilterComponent/GlobalFilterComponent.utils';
import { useQueryGetMatrixData } from 'api/queries/getMatrixData.query';
import { useQueryPrioritiesMatrixCards } from 'api/queries/prioritiesMatrixCards.query';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { ProgramStatusUrlParams } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/ProgramStatusSummaryReport/programStatusSummaryReportTypes';
import { useGlobalFiltersHook } from 'hooks/useGlobalFiltersHook';

export const usePrioritiesMatrixComponentProvider = ({
    widgetId,
    contractId,
    projectId,
    latestVersionId,
    globalFilterData,
    matrixFilterUrlParam,
    matrixCardCategoryId,
    isOverdueFilter,
    isDelayDriverFilter,
    isAssignedToMeFilter,
    isCriticalPathFilter,
    selectedTypeAndRole,
    localFilters,
}) => {
    const { id: versionId } = useParams<ProgramStatusUrlParams>();

    const source = widgetId
        ? localFilters
        : {
              ...globalFilterData,
              numOfDaysToBeUrgent: Number(matrixFilterUrlParam),
              isOverdueFilter,
              isDelayDriverFilter,
              isAssignedToMeFilter,
              isCriticalPathFilter,
              selectedTypeAndRole,
              versionId,
          };

    const filters = useMemo(
        () =>
            getFiltersData({
                [FilterNames.ACTIVITY_CODES]: source?.activityCodes
                    ?.filter((item) => !item.value.isExclude)
                    .map((i) => i.value.name),
                [FilterNames.ACTIVITY_CODES_EXCLUDE]: source?.activityCodes
                    ?.filter((item) => item.value.isExclude)
                    .map((i) => i.value.name),
                [FilterNames.FRAGNET_MILESTONE_ID_CARD_BOARD]: source?.milestoneFragnet,
                [FilterNames.TAG_CARD_BOARD]: source?.tags
                    ?.filter((item) => !item.value.isExclude)
                    .map((i) => i.value.name),
                [FilterNames.TAG_CARD_BOARD_EXCLUDE]: source?.tags
                    ?.filter((item) => item.value.isExclude)
                    .map((i) => i.value.name),
                [FilterNames.WBS_SEARCH]: source?.wbs,
            }),

        [source.activityCodes, globalFilterData],
    );

    const state = {
        project: {
            projectId,
            contractId,
            latestVersionId,
        },
        globalFilter: {
            tags: source?.tags,
            wbs: source?.wbs,
            milestoneFragnet: source?.milestoneFragnet,
            activityCodes: source?.activityCodes,
        },
        local: {
            numOfDaysToBeUrgent: source.numOfDaysToBeUrgent,
            isOverdueFilter: source.isOverdueFilter,
            isDelayDriverFilter: source.isDelayDriverFilter,
            isAssignedToMeFilter: source.isAssignedToMeFilter,
            isCriticalPathFilter: source.isCriticalPathFilter,
            selectedTypeAndRole: source.selectedTypeAndRole,
            versionId: source.versionId,
        },
    };

    // create pill data for PinboardWidgetPills component
    const pillsData = {
        numOfDaysToBeUrgent: source.numOfDaysToBeUrgent,
        wbs: source?.wbs,
        milestoneFragnet: source?.milestoneFragnet,
        activityCodes: source?.activityCodes?.filter((item) => !item.value?.isExclude).map((i) => i.value?.name),
        activityCodesExclude: source?.activityCodes?.filter((item) => item.value?.isExclude).map((i) => i.value?.name),
        tags: source?.tags?.filter((item) => !item.value?.isExclude).map((i) => i.value?.name),
        tagsExclude: source?.tags?.filter((item) => item.value?.isExclude).map((i) => i.value?.name),
    };
    const { pillsList: pills } = useGlobalFiltersHook({ widgetId, source: pillsData, latestVersionId });

    //queries
    const matrixDataPayload = {
        versionId: latestVersionId,
        numOfDaysToBeUrgent: source.numOfDaysToBeUrgent,
        filter: {
            filters,
        },
    };

    const prioritiesMatrixCardPayload = {
        ...matrixDataPayload,
        itemId: matrixCardCategoryId,
    };

    return {
        ui: {
            state: {
                ...state.project,
                ...state.globalFilter,
                ...state.local,
            },
            pills,
            flatPills: { ...pillsData },
        },
        api: {
            matrixData: useQueryGetMatrixData(matrixDataPayload),
            cards: useQueryPrioritiesMatrixCards(prioritiesMatrixCardPayload),
        },
    };
};
